import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { getLang, localeOf, useT } from '../lib/i18n';
import { haptic } from '../lib/telegram';
import { toCoachDateKey } from '../lib/time';

/**
 * The month, for jumping to a date.
 *
 * The day strip is for the days either side of today. A coach looking
 * for last month's session with someone, or a date in November, needs
 * the whole month at once. A dot says there were sessions that day; a
 * bar says one of them is still unpaid. The two differ in shape as well
 * as colour, so they read apart without the colour.
 *
 * Unpaid only counts once the session has started. Before that every
 * cash booking is unpaid, which is normal and not worth a mark.
 */

export interface MonthSession {
  starts_at: string;
  bookings: { paymentMode: 'credits' | 'cash'; paidAt: string | null }[];
}

/** How far the arrows go: the two years the calendar feed keeps, and the year ahead it shows. */
const MONTHS_BACK = 24;
const MONTHS_AHEAD = 12;

const pad = (n: number) => String(n).padStart(2, '0');

/** Monday-first initials, from the language rather than hardcoded. */
const weekdayInitials = () => {
  const base = new Date(2024, 0, 1); // a Monday
  return Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(localeOf(getLang()), { weekday: 'narrow' })
      .format(new Date(base.getFullYear(), base.getMonth(), base.getDate() + i)));
};

/** "September 2026" / "сентябрь 2026", without Russian's trailing "г.". */
const monthTitle = (year: number, month: number) => {
  const d = new Date(Date.UTC(year, month, 15));
  const name = new Intl.DateTimeFormat(localeOf(getLang()), { month: 'long', timeZone: 'UTC' }).format(d);
  return `${name} ${year}`;
};

export function MonthSheet({ selected, fetchMonth, onPick, onClose }: {
  selected: string;
  fetchMonth: (from: string, to: string) => Promise<MonthSession[]>;
  onPick: (key: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const todayKey = toCoachDateKey(new Date());
  const [ty, tm] = todayKey.split('-').map(Number);
  const [year, setYear] = useState(() => Number(selected.slice(0, 4)));
  const [month, setMonth] = useState(() => Number(selected.slice(5, 7)) - 1);
  const [sessions, setSessions] = useState<MonthSession[] | null>(null);

  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const offset = (year - ty) * 12 + month - (tm - 1);

  useEffect(() => {
    let live = true;
    setSessions(null);
    fetchMonth(`${year}-${pad(month + 1)}-01`, `${year}-${pad(month + 1)}-${pad(daysInMonth)}`)
      .then((rows) => { if (live) setSessions(rows); })
      .catch(() => { if (live) setSessions([]); });
    return () => { live = false; };
  }, [year, month, daysInMonth, fetchMonth]);

  const marks = useMemo(() => {
    const had = new Set<string>(), owed = new Set<string>();
    const now = Date.now();
    for (const s of sessions ?? []) {
      if (s.bookings.length === 0) continue;
      const key = toCoachDateKey(new Date(s.starts_at));
      had.add(key);
      if (new Date(s.starts_at).getTime() <= now
        && s.bookings.some((b) => b.paymentMode === 'cash' && !b.paidAt)) owed.add(key);
    }
    return { had, owed };
  }, [sessions]);

  // Monday-first: how many blanks before the 1st.
  const leading = (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7;

  function go(delta: number) {
    haptic.tap();
    const m = month + delta;
    setYear(year + Math.floor(m / 12));
    setMonth(((m % 12) + 12) % 12);
  }

  return createPortal(
    <div className="sheet" onClick={onClose}>
      <div className="sheet__panel" onClick={(e) => e.stopPropagation()}>
        <div className="mcal__head">
          <div className="mcal__title">{monthTitle(year, month)}</div>
          <button className="mcal__btn mcal__btn--today" onClick={() => { haptic.tap(); onPick(todayKey); }}>
            {t('day.today')}
          </button>
          <button className="mcal__btn" aria-label={t('co.month.prev')}
            disabled={offset <= -MONTHS_BACK} onClick={() => go(-1)}>‹</button>
          <button className="mcal__btn" aria-label={t('co.month.next')}
            disabled={offset >= MONTHS_AHEAD} onClick={() => go(1)}>›</button>
        </div>

        <div className="mcal__grid">
          {weekdayInitials().map((w, i) => <div key={i} className="mcal__wd">{w}</div>)}
          {Array.from({ length: leading }, (_, i) => <div key={`b${i}`} />)}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const key = `${year}-${pad(month + 1)}-${pad(i + 1)}`;
            const cls = ['mcal__day',
              key < todayKey && 'mcal__day--past',
              key === todayKey && 'mcal__day--today',
              key === selected && 'mcal__day--sel'].filter(Boolean).join(' ');
            return (
              <button key={key} className={cls} onClick={() => { haptic.tap(); onPick(key); }}>
                <b>{i + 1}</b>
                <span className="mcal__marks">
                  {marks.had.has(key) && <i className="mcal__dot" />}
                  {marks.owed.has(key) && <i className="mcal__owed" />}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mcal__legend">
          <span><i className="mcal__dot" />{t('co.month.sessions')}</span>
          <span><i className="mcal__owed" />{t('co.month.unpaid')}</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
